import { Separator, Text } from '@cuemath/web-comps';
import React from 'react';
import {
  Button,
  Form,
  Icon,
  Modal,
  ModalActions,
  ModalContent,
  ModalDescription,
  ModalHeader,
  Segment,
  TextArea,
} from 'semantic-ui-react';
import { Col, Row } from '@cuemath/comps';
import styled from 'styled-components';

import COLORS from '../../../constants/colors';
import { usePostEvalSpecialistPause } from 'actions/eval-reporting';
import {
  formatIstDateTime,
  isPastIstInput,
  istLocalInputToEpoch,
} from '../common/constants';
import HalfHourPicker from '../common/half-hour-picker';

const ContextCard = styled(Col)`
  background: ${COLORS.WILD_SAND};
  border-radius: 8px;
`;

const ContextRow = styled(Row)`
  padding: 12px 16px;
`;

const ContextRowTight = styled(Row)`
  padding: 0 16px 12px;
`;

const AddPause = ({ specialist, closeModal, onApplied }) => {
  const [startInput, setStartInput] = React.useState('');
  const [endInput, setEndInput] = React.useState('');
  const [reason, setReason] = React.useState('');
  const [pauseError, setPauseError] = React.useState(null);
  const [pauseResult, setPauseResult] = React.useState(null);

  const { post: postPause, isProcessing: isApplying } = usePostEvalSpecialistPause({
    onComplete: (error, data) => {
      if (error) {
        setPauseError(error || 'Could not apply the pause.');

        return;
      }
      setPauseError(null);
      setPauseResult(data || {});
    },
  });

  const startEpoch = istLocalInputToEpoch(startInput);
  const endEpoch = istLocalInputToEpoch(endInput);
  const isRangeValid = Boolean(startEpoch && endEpoch && endEpoch > startEpoch);
  // A pause that has already elapsed protects nothing and only muddies the roster.
  const startsInPast = isPastIstInput(startInput);
  const canApply =
    isRangeValid && !startsInPast && Boolean(reason.trim()) && !isApplying;

  const handleApply = React.useCallback(() => {
    setPauseError(null);
    postPause(
      {
        start_datetime: startEpoch,
        end_datetime: endEpoch,
        reason: reason.trim(),
      },
      specialist.teacher_id,
    );
  }, [postPause, startEpoch, endEpoch, reason, specialist.teacher_id]);

  const handleDone = React.useCallback(() => {
    if (pauseResult && onApplied) onApplied();
    closeModal();
  }, [pauseResult, onApplied, closeModal]);

  const renderSuccess = () => (
    <Segment>
      <Text variant="body1" color={COLORS.DARK_GREEN}>
        <Icon name="check circle" /> Pause applied for {specialist.teacher_name || 'this specialist'}.
      </Text>
      <Separator height={12} transparent />
      <Text variant="body2">
        {formatIstDateTime(startEpoch)} → {formatIstDateTime(endEpoch)}
      </Text>
      <Separator height={8} transparent />
      <Text variant="body3" color={COLORS.GRAY}>
        Eval slots in this window are no longer bookable. No leave units were consumed.
      </Text>
    </Segment>
  );

  const renderForm = () => (
    <Form>
      <Text variant="body2">Pause start (IST)</Text>
      <Separator height={4} transparent />
      <HalfHourPicker
        value={startInput}
        onChange={value => {
          setStartInput(value);
          setPauseError(null);
        }}
        ariaLabel="Pause start"
      />
      <Separator height={12} transparent />
      <Text variant="body2">Pause end (IST)</Text>
      <Separator height={4} transparent />
      <HalfHourPicker
        value={endInput}
        onChange={value => {
          setEndInput(value);
          setPauseError(null);
        }}
        ariaLabel="Pause end"
      />
      {startsInPast && (
        <>
          <Separator height={8} transparent />
          <Text variant="body3" color={COLORS.RUSTY_RED}>
            The pause start is in the past. Pick a time from now onwards.
          </Text>
        </>
      )}
      {Boolean(startInput && endInput) && !isRangeValid && (
        <>
          <Separator height={8} transparent />
          <Text variant="body3" color={COLORS.RUSTY_RED}>
            The end must be after the start.
          </Text>
        </>
      )}
      <Separator height={12} transparent />
      <Text variant="body2">Reason</Text>
      <Separator height={4} transparent />
      <TextArea
        rows={3}
        value={reason}
        placeholder="Why is this specialist off?"
        onChange={(_, { value }) => {
          setReason(value);
          setPauseError(null);
        }}
        aria-label="Pause reason"
      />
      {isRangeValid && (
        <>
          <Separator height={12} transparent />
          <Text variant="body3" color={COLORS.GRAY}>
            {formatIstDateTime(startEpoch)} → {formatIstDateTime(endEpoch)}
          </Text>
        </>
      )}
    </Form>
  );

  const renderActions = () => {
    if (pauseResult) {
      return (
        <Button color="black" onClick={handleDone}>
          Done
        </Button>
      );
    }

    return (
      <>
        <Button basic onClick={closeModal}>
          Cancel
        </Button>
        <Button
          color="black"
          loading={isApplying}
          disabled={!canApply}
          onClick={handleApply}
        >
          Apply Pause
        </Button>
      </>
    );
  };

  return (
    <Modal open size="small" onClose={closeModal}>
      <ModalHeader>Add a pause</ModalHeader>
      <ModalContent>
        <ModalDescription>
          <ContextCard gutter={0}>
            <ContextRow gutter={0}>
              <Text variant="body2">{specialist.teacher_name || 'Unnamed specialist'}</Text>
            </ContextRow>
            <ContextRowTight gutter={0}>
              <Text variant="body3" color={COLORS.GRAY}>
                {specialist.teacher_id}
              </Text>
            </ContextRowTight>
          </ContextCard>

          <Separator height={16} transparent />
          {pauseResult ? renderSuccess() : renderForm()}

          {pauseError && (
            <>
              <Separator height={12} transparent />
              <Text variant="body2" color={COLORS.RUSTY_RED}>
                {pauseError}
              </Text>
            </>
          )}
        </ModalDescription>
      </ModalContent>
      <ModalActions>{renderActions()}</ModalActions>
    </Modal>
  );
};

export default AddPause;
